import { Switch } from "@/components/ui/switch";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import {
  backofficeLinks,
  essentialMenuItem,
} from "@/components/lib/SidebarLinks";
import { Link, useParams, useNavigate } from "react-router-dom";
import { z } from "zod";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState, useMemo, useEffect } from "react";
import useAuth from "@/hooks/useAuth";
import {
  updateExistingCategory,
  fetchSingleCategory,
  clearCategoryMessages,
} from "@/redux/slice/essential-menu-slice/category-slice";
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import CreateDrinksSkeleton from "@/components/skeleton/create-menu-skeleton";

const addNewCgtSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  order: z
    .union([z.string(), z.number()])
    .transform((val) => (val === "" ? undefined : Number(val)))
    .refine((val) => val === undefined || Number.isInteger(val), {
      message: "View order must be an integer",
    })
    .optional(),
  status: z.enum(["active", "inactive", "archive", "draft"]).default("active"),
});

function EssentialUpdateCtg() {
  const { user } = useAuth();
  const { id, item_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateLoading, error, singleCategory, getLoading, categories } =
    useSelector((state) => state.essentialMenu.categories);
  const [showIsChaild, setShowIsChaild] = useState(false);

  const form = useForm({
    resolver: zodResolver(addNewCgtSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      order: "",
      menu_item_id: id,
      parent_id: null,
      status: "active",
    },
  });
  console.log(singleCategory);
  const fetchOnly = useMemo(() => ["categories"], []);

  const { groupLoading } = useFetchAllMenuData(user.restaurant_id, id, {
    fetchOnly,
  });

  const matchedItem = useMemo(() => {
    return essentialMenuItem.find((item) => item.id === Number(id));
  }, [id]);

  useEffect(() => {
    if (item_id) {
      dispatch(fetchSingleCategory(item_id));
    }
  }, [item_id, dispatch]);

  useEffect(() => {
    if (singleCategory?.parent) {
      setShowIsChaild(true);
    } else {
      setShowIsChaild(false);
    }
  }, [singleCategory?.parent]);

  useEffect(() => {
    if (singleCategory) {
      form.reset({
        name: singleCategory.name || "",
        description: singleCategory.description || "",
        order: singleCategory?.parent?.order || singleCategory?.order || "",
        parent_id: singleCategory.parent_id ?? null,
        status: singleCategory.status || "active",
        menu_item_id: singleCategory.menu_item_id,
      });
    }
  }, [singleCategory, form]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      menu_item_id: Number(id),
      client_id: user?.client_id,
      restaurant_id: user?.restaurant_id,
    };

    try {
      dispatch(clearCategoryMessages());
      const res = await dispatch(
        updateExistingCategory({ id: item_id, data: payload })
      );
      if (updateExistingCategory.fulfilled.match(res)) {
        toast.success("Category updated successfully.");
        form.reset();
        dispatch(clearCategoryMessages());
        if (matchedItem) {
          navigate(`${matchedItem.path}/${id}`);
        } else {
          navigate("backoffice/menu/essentail-menu/drinks/1");
        }
      } else if (updateExistingCategory.rejected.match(res)) {
        toast.error(res?.payload);
      }
    } catch (error) {
      toast.error("Something went wrong.");
    }
  };

  if ((getLoading, groupLoading)) return <CreateDrinksSkeleton />;

  return (
    <Layout2
      pageName={{ title: "Update Category" }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: `${matchedItem?.title}`, path: `${matchedItem?.path}/${id}` },
        {
          name: `Update Category`,
          path: `${matchedItem?.path}/${id}/update`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <Card className="max-w-screen-md bg-cblack6 mx-auto mt-10 text-cwhite">
        <CardHeader>
          <CardTitle className="text-lg">Update Category</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-7"
            >
              {/* Name */}
              <FormInputField
                name="name"
                style="bg-cblack7"
                placeholder="Name"
                form={form}
                type="text"
                label="Name"
              />

              {/* Description */}
              <FormInputField
                name="description"
                style="bg-cblack7"
                placeholder="Add note"
                form={form}
                type="text"
                label="Description"
              />

              <div className="space-y-4">
                <div className="flex gap-2 items-center">
                  <Switch
                    className="data-[state=unchecked]:bg-neutral-500"
                    checked={showIsChaild}
                    onCheckedChange={(val) => {
                      setShowIsChaild(val);
                      if (!val) {
                        form.setValue("parent_id", null);
                      }
                    }}
                  />
                  <Label>Is Child</Label>
                </div>

                {showIsChaild && (
                  <FormInputField
                    name="parent_id"
                    style="bg-cblack7 flex-1"
                    placeholder="Select Category"
                    form={form}
                    type="select"
                    parseAs="number"
                    search={true}
                    selectOption={
                      categories?.length
                        ? categories.filter((item) => item?.status === "active").map((item) => ({
                            label: item?.name,
                            value: item?.id,
                          }))
                        : [
                            {
                              label: "No Menu available",
                              value: "",
                              disabled: true,
                            },
                          ]
                    }
                    label="Parent Category"
                  />
                )}
              </div>

              <FormInputField
                name="order"
                style="bg-cblack7"
                placeholder="0"
                form={form}
                type="text"
                label="View Order"
              />

              {/* Status */}
              <FormInputField
                name="status"
                style="bg-cblack7 h-10"
                placeholder="Select Status"
                form={form}
                type="select"
                selectOption={[
                  { label: "Select Status", value: "", disabled: true },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
                label="Status"
              />

              {/* Buttons */}
              <div className="w-full flex gap-5 items-center justify-between mt-5">
                <Link className="w-full" to={`${matchedItem.path}/${id}`}>
                  <Button className="bg-cblack7 w-full text-cwhite">
                    Cancel
                  </Button>
                </Link>
                <Button
                  isLoading={updateLoading}
                  type="submit"
                  className="bg-cgreen text-white w-full"
                >
                  Update
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </Layout2>
  );
}

export default EssentialUpdateCtg;
